import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { chipContentPadding, chipTextClass, useFilterBarDensity } from './filterBarDensity';
import { MultiSelect } from 'primereact/multiselect';
import { Dropdown } from '@components/RingilComponents/SafeDropdown';
import { Skeleton } from 'primereact/skeleton';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import SvgChevron from '@icons/chevron.svg?react';
import SvgClose from '@icons/close.svg?react';
import CustomTooltip from '@components/ringil3/Tooltip/CustomTooltip';
import { FilterConfig, FilterType, OptionType } from '../types';
import { clearFilter, FILTER_APPLY_DELAY_MS, getFilterKey, getFilterLabel, getFilterValueLabel, isFilterActive, renderChipValue, sameValue } from './filterChipUtils';

interface Props {
  conf: FilterConfig;
  filter: any;
  applyFilterValue: (key: string, value: any) => void;
  loading: boolean;
  autoOpen?: boolean;
  onClosed?: (active: boolean) => void;
  /** Let the value wrap to multiple lines instead of truncating at 200px. */
  wrap?: boolean;
  /** Hide the field label inside the chip; inactive chips show a "Not selected" placeholder instead. */
  hideLabel?: boolean;
}

const readKey = (filter: any, key: string) => (filter && typeof filter === 'object' && key in filter ? filter[key] : undefined);

/**
 * Filter chip for Dropdown / MultiSelect filters. The chip *is* the (invisible, full-bleed) select,
 * so clicking it opens the select panel directly (no intermediate overlay). The visible text is our
 * own: the field label (bold once a value is selected) followed by the selected value(s) in normal weight.
 */
export const SelectFilterChip: React.FC<Props> = ({ conf, filter, applyFilterValue, loading, autoOpen, onClosed, wrap, hideLabel }) => {
  const { tr } = useTranslationLgs();
  const [open, setOpen] = useState(false);
  const selectRef = useRef<any>(null);

  const isMulti = conf.filterType === FilterType.MultiSelect || conf.filterType === FilterType.MultiSelectLazy;
  const isLazy = conf.filterType === FilterType.MultiSelectLazy || conf.filterType === FilterType.DropdownLazy;
  const label = getFilterLabel(conf);
  const value = readKey(filter, conf.value[0]);

  // Draft drives the chip + panel and updates immediately on every selection. The real filter (API) is
  // applied on a debounce (fires even while open), only when the value actually changed; a still-pending
  // change is flushed on close so it isn't lost (no double-apply since the debounce clears `pending`).
  const [draft, setDraft] = useState(value);
  const valueRef = useRef(value);
  valueRef.current = value;
  const applyTimer = useRef<ReturnType<typeof setTimeout>>();
  const pending = useRef(false);

  // The chip reflects the draft right away (not the applied filter).
  const active = isFilterActive(conf, { [conf.value[0]]: draft });
  const valueLabel = active ? getFilterValueLabel(conf, { [conf.value[0]]: draft }, tr) : '';

  // Re-sync the draft whenever the filter changes from outside (reset, saved filter, clear ✕).
  useEffect(() => {
    setDraft(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(value ?? null)]);

  useEffect(() => () => clearTimeout(applyTimer.current), []);

  const scheduleApply = (next: any) => {
    setDraft(next); // chip + panel update immediately
    clearTimeout(applyTimer.current);
    if (sameValue(next, valueRef.current)) {
      pending.current = false; // back to the applied value — nothing to refresh
      return;
    }
    pending.current = true;
    applyTimer.current = setTimeout(() => {
      pending.current = false;
      if (!sameValue(next, valueRef.current)) applyFilterValue(conf.value[0], next);
    }, FILTER_APPLY_DELAY_MS);
  };

  const clearNow = () => {
    clearTimeout(applyTimer.current);
    pending.current = false;
    setDraft(isMulti ? [] : null);
    clearFilter(conf, applyFilterValue);
  };

  useEffect(() => {
    if (autoOpen) selectRef.current?.show?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // MultiSelect keyboard: items already handle ↑/↓ navigate + space toggle, but Enter toggles the item
  // (we want it to confirm + close) and ↓ from the filter input doesn't reach the list. Fix both here.
  // Capture phase so we intercept Enter before the option's own keydown toggles it.
  useEffect(() => {
    if (!open || !isMulti) return;
    const onKeyDown = (e: KeyboardEvent) => {
      const panel = document.querySelector<HTMLElement>('.p-multiselect-panel');
      if (!panel || !panel.contains(e.target as Node)) return;
      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        selectRef.current?.hide?.();
      } else if (e.key === 'ArrowDown' && (e.target as HTMLElement).classList.contains('p-multiselect-filter')) {
        panel.querySelector<HTMLElement>('.p-multiselect-item')?.focus();
        e.preventDefault();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [open, isMulti]);

  const renderOption = (option: OptionType) => (
    <span className={clsx('inline-flex items-center gap-2', !option.active && 'line-through text-content-tertiary')}>
      {option.icon}
      {option.label}
    </span>
  );

  // The select is invisible and fills the chip purely to capture clicks + host the panel.
  // `!absolute` is required: GlobalStyles sets position/width/height on .p-dropdown/.p-multiselect with
  // equal specificity, so without !important the select stays in flow and adds empty width + its own height.
  const selectClassName = '!absolute !inset-0 z-0 !w-full !h-full !min-h-0 !opacity-0 cursor-pointer';
  const commonProps = {
    ref: selectRef,
    options: conf.options,
    optionLabel: 'label',
    optionValue: 'value',
    filter: true,
    // Portal the panel to <body> — otherwise it renders inside the opacity-0 select and is invisible.
    appendTo: typeof document !== 'undefined' ? document.body : undefined,
    onShow: () => {
      setOpen(true);
      // The portaled panel just mounted — focus its search input so the user can type immediately.
      requestAnimationFrame(() => {
        const input = document.querySelector<HTMLInputElement>('.p-multiselect-panel .p-multiselect-filter, .p-dropdown-panel .p-dropdown-filter');
        // preventScroll: the panel is portaled to <body>; a plain focus() makes the browser (esp. Edge)
        // scroll the document down to the portaled element, leaving an unrecoverable blank gap.
        input?.focus({ preventScroll: true });
      });
    },
    onHide: () => {
      setOpen(false);
      // Flush a still-pending change so it isn't lost on a quick close; if the debounce already fired,
      // `pending` is false and we don't refresh again.
      if (pending.current) {
        clearTimeout(applyTimer.current);
        pending.current = false;
        if (!sameValue(draft, valueRef.current)) applyFilterValue(conf.value[0], draft);
      }
      onClosed?.(isFilterActive(conf, { [conf.value[0]]: draft }));
    },
    itemTemplate: (option: OptionType) => renderOption(option),
    className: selectClassName,
    // Gap between the chip trigger and the portaled panel; ringil-filter-panel matches
    // RingilDropdown's row highlighting (see styles.scss).
    panelClassName: '!mt-1 ringil-filter-panel',
  };

  // PrimeReact only forwards the search text on a *scroll*-driven lazy load, and typing leaves the
  // scroller at index 0 — so with client-side filtering off (lazy) the search box would do nothing
  // unless the query is pushed through onFilter. MultiSelectLazy is left alone: its only consumer
  // ignores `filter` and would just append pages per keystroke.
  const filterTimer = useRef<ReturnType<typeof setTimeout>>();
  const onFilter =
    conf.filterType === FilterType.DropdownLazy
      ? (e: { filter: string }) => {
          clearTimeout(filterTimer.current);
          filterTimer.current = setTimeout(() => conf.onLazyLoad?.({ first: 0, last: 0, filter: e.filter }), 300);
        }
      : undefined;
  useEffect(() => () => clearTimeout(filterTimer.current), []);

  // `loading` is the table's; conf.lazyLoading is the option list's, which is what the panel reflects.
  const virtualScrollerOptions = isLazy
    ? {
        lazy: true,
        onLazyLoad: conf.onLazyLoad,
        itemSize: 38,
        showLoader: true,
        loading: conf.lazyLoading ?? loading,
        loadingTemplate: (options: any) => (
          <div style={{ height: '50px' }}>
            <Skeleton width={options.even ? '60%' : '50%'} height="1.3rem" />
          </div>
        ),
      }
    : undefined;

  const density = useFilterBarDensity();
  return (
    <div
      className={clsx(
        "relative inline-flex items-center rounded border border-solid font-['DM_Sans']",
        chipTextClass(density),
        active ? 'border-content-brand bg-content-brandMedium' : 'border-content-brandMedium bg-content-negativePrimary',
        wrap && 'min-w-0',
      )}
      data-testid={`filter-chip-${getFilterKey(conf)}`}
    >
      {isMulti ? (
        <MultiSelect {...commonProps} value={Array.isArray(draft) ? draft : []} onChange={e => scheduleApply(e.value)} virtualScrollerOptions={virtualScrollerOptions} />
      ) : (
        <Dropdown {...commonProps} value={draft} onChange={e => scheduleApply(e.value)} virtualScrollerOptions={virtualScrollerOptions} onFilter={onFilter} />
      )}

      {/* Visible content (clicks fall through to the select below; only the ✕ is interactive) */}
      <div className={clsx('pointer-events-none relative z-10 inline-flex items-center gap-1.5', chipContentPadding(density))}>
        {!hideLabel && <span className="whitespace-nowrap font-normal text-content-primary">{label}</span>}
        {active ? (
          <span className={clsx('text-content-primary', wrap ? 'min-w-0 break-words' : 'max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap')}>
            {renderChipValue(valueLabel)}
          </span>
        ) : (
          hideLabel && <span className="whitespace-nowrap font-normal text-content-secondary">{tr('RingilDataTable.notSelected', 'Not selected')}</span>
        )}
        <SvgChevron className={clsx('w-3 h-3 shrink-0 text-content-brand transition-transform', open && 'rotate-180')} />
        {active && (
          <>
            <span className="shrink-0 text-content-tertiary">|</span>
            <SvgClose
              className="pointer-events-auto w-3 h-3 shrink-0 text-complementary-error cursor-pointer"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                clearNow();
              }}
              data-testid={`filter-chip-clear-${getFilterKey(conf)}`}
            />
          </>
        )}
      </div>
      {active && !open && <CustomTooltip targetSelector={`[data-testid="filter-chip-${getFilterKey(conf)}"]`} content={valueLabel} position="bottom" />}
    </div>
  );
};
